import React, { useEffect, useState } from "react";

/**
 * Official Rendeavored logo (single reusable component).
 *
 * Uses the exact uploaded official assets (never recreated/approximated):
 *  - Dark surfaces  -> black-background asset rendered with mix-blend-mode:
 *    "screen", which drops the solid black backdrop and leaves the crisp white
 *    logo on any dark surface (no file modification).
 *  - Light surfaces -> transparent dark-wordmark asset (rendeavored_light.png),
 *    which is the color logo on a transparent canvas sized to the SAME
 *    footprint (1440x365) as the dark asset so dimensions never shift.
 *
 * Theme behaviour:
 *  - By default the component keeps its explicit `variant` (backwards
 *    compatible — public marketing branding is untouched).
 *  - When `themeAware` is set (used in the authenticated/admin interface), the
 *    logo follows the active theme resolved on <html data-theme="..."> and
 *    switches instantly when the user changes Light / Dark / System — no page
 *    refresh — via a MutationObserver on that attribute.
 *
 * Aspect ratio is always preserved (height set, width auto).
 */
const ASSETS = {
  dark: { src: "/assets/rendeavored_black.jpg", blend: "screen" },
  light: { src: "/assets/rendeavored_light.png", blend: "normal" },
  color: { src: "/assets/rendeavored_color.png", blend: "normal" },
};

const readResolvedTheme = () => {
  if (typeof document === "undefined") return "dark";
  return document.documentElement.getAttribute("data-theme") === "light"
    ? "light"
    : "dark";
};

export const Logo = ({
  height = 34,
  className = "",
  variant = "dark",
  themeAware = false,
}) => {
  const [resolved, setResolved] = useState(readResolvedTheme);

  useEffect(() => {
    if (!themeAware || typeof document === "undefined") return undefined;
    setResolved(readResolvedTheme());
    const observer = new MutationObserver(() => setResolved(readResolvedTheme()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, [themeAware]);

  // themeAware -> dark/light asset by active theme; otherwise honour `variant`.
  let key;
  if (themeAware) {
    key = resolved === "light" ? "light" : "dark";
  } else {
    key = variant === "dark" ? "dark" : "color";
  }
  const { src, blend } = ASSETS[key];

  return (
    <img
      src={src}
      alt="Rendeavored"
      style={{
        height: `${height}px`,
        width: "auto",
        display: "block",
        mixBlendMode: blend,
      }}
      className={className}
      draggable={false}
    />
  );
};

export default Logo;
